import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { cmpDoneDesc } from "./taskUtils";

export const APPROVED_PAGE = 50;

// Sloučení zdrojů bez duplicit — první výskyt vyhrává (rozpracované jsou nejčerstvější:
// vrácený schválený úkol se tak ukáže jako rozpracovaný, ne dvakrát).
export function mergeTasks(...lists) {
  const seen = new Set();
  const out = [];
  lists.forEach((list) => (list || []).forEach((t) => { if (!seen.has(t.id)) { seen.add(t.id); out.push(t); } }));
  return out;
}

// Hranice načtené historie: dokud server má starší schválené, ukazuje se jen souvislý
// úsek od nejnovějšího po poslední načtený — jinak by v Historii byly díry.
export const aboveFloor = (floor) => (t) => !floor || cmpDoneDesc(t, floor) <= 0;

/**
 * Data stránky Úkoly (218). Dřív jeden GET /tasks s celou historií firmy, teď:
 *  - rozpracované (Nový, V řešení, Ke schválení) celé: GET /tasks?status=open,
 *  - schválené po stránkách: GET /tasks/approved (Historie, sloupec Hotovo v Kanbanu),
 *    `approvedQuery` = rozsah a filtry, které umí server,
 *  - v kalendáři schválené jen pro zobrazené okno: GET /tasks?status=approved&date_from&date_to.
 * reload() obnoví všechno najednou (načtené stránky historie zůstanou rozbalené).
 */
export function useTaskLists({ approvedQuery, calWindow }) {
  const [open, setOpen] = useState(null);        // null = ještě se načítá
  const [failed, setFailed] = useState(false);
  const [appr, setAppr] = useState({ key: null, items: [], next: null, total: 0 });
  const [apprBusy, setApprBusy] = useState(false);
  const [cal, setCal] = useState({ key: null, items: [] });
  const seq = useRef({ open: 0, appr: 0, cal: 0 });
  const latest = useRef({});
  latest.current = { appr, approvedQuery, calWindow };

  const loadOpen = useCallback(() => {
    const s = ++seq.current.open;
    return api.get("/tasks?status=open")
      .then((r) => { if (s === seq.current.open) { setOpen(r.data || []); setFailed(false); } })
      .catch(() => { if (s === seq.current.open) setFailed(true); });
  }, []);

  const loadApproved = useCallback((key, { before = null, limit = APPROVED_PAGE, append = false } = {}) => {
    const s = ++seq.current.appr;
    setApprBusy(true);
    const qs = [key, `limit=${limit}`, before ? `before=${encodeURIComponent(before)}` : ""].filter(Boolean).join("&");
    return api.get(`/tasks/approved?${qs}`)
      .then((r) => {
        if (s !== seq.current.appr) return;
        const d = r.data || {};
        setAppr((prev) => ({
          key,
          items: append && prev.key === key ? mergeTasks(prev.items, d.items) : (d.items || []),
          next: d.next || null,
          total: d.scope_total || 0,
        }));
      })
      .catch(() => { /* historie se doplní při dalším načtení */ })
      .finally(() => { if (s === seq.current.appr) setApprBusy(false); });
  }, []);

  const loadCal = useCallback((w) => {
    const s = ++seq.current.cal;
    const key = `${w.from}|${w.to}`;
    return api.get(`/tasks?status=approved&date_from=${w.from}&date_to=${w.to}`)
      .then((r) => { if (s === seq.current.cal) setCal({ key, items: r.data || [] }); })
      .catch(() => {});
  }, []);

  useEffect(() => { loadOpen(); }, [loadOpen]);
  useEffect(() => { loadApproved(approvedQuery); }, [approvedQuery, loadApproved]);
  const calFrom = calWindow?.from, calTo = calWindow?.to;
  useEffect(() => { if (calFrom && calTo) loadCal({ from: calFrom, to: calTo }); }, [calFrom, calTo, loadCal]);

  const reload = useCallback(() => {
    const { appr: a, approvedQuery: key, calWindow: w } = latest.current;
    const span = a.key === key ? Math.max(APPROVED_PAGE, a.items.length) : APPROVED_PAGE;
    return Promise.all([loadOpen(), loadApproved(key, { limit: Math.min(span, 1000) }), w ? loadCal(w) : null]);
  }, [loadOpen, loadApproved, loadCal]);

  const loadMoreApproved = useCallback(() => {
    const { appr: a, approvedQuery: key } = latest.current;
    if (!a.next || a.key !== key || apprBusy) return;
    loadApproved(key, { before: a.next, append: true });
  }, [apprBusy, loadApproved]);

  const tasks = useMemo(() => (open ? mergeTasks(open, appr.items, cal.items) : []), [open, appr.items, cal.items]);
  const floor = appr.next && appr.items.length ? appr.items[appr.items.length - 1] : null;

  return {
    tasks, openTasks: open, loading: open === null && !failed, failed,
    setOpenTasks: setOpen, reload, retry: reload,
    approved: { hasMore: !!appr.next, total: appr.total, fresh: appr.key === approvedQuery, busy: apprBusy, floor },
    loadMoreApproved,
  };
}
